import { FC, useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  DialogProps,
} from "@mui/material";

import { makeSxStyles } from "ui";
import { universalApiSettingsToUIFormat } from "../../../shared/utils/route-notification-utils";
import { NOTIFICATION_TYPES_ARRAY } from "../types/notification-types-enum";
import {
  AdminGetDateRouteNotificationsResDto,
  AdminGetRouteNotificationsResDto,
} from "service/api/estabrookSchemas";

interface AutomatedRouteNotificationsModalProps
  extends Omit<DialogProps, "onClose"> {
  open: boolean;
  description: string;
  onClose: () => void;
  onSave?: (settings: Record<string, boolean>) => void;
  currentSettings?:
    | AdminGetDateRouteNotificationsResDto
    | AdminGetRouteNotificationsResDto;
  globalNotificationSettings?: AdminGetDateRouteNotificationsResDto;
  isGlobal?: boolean;
}

export const AutomatedRouteNotificationsModal: FC<
  AutomatedRouteNotificationsModalProps
> = ({
  open,
  onClose,
  onSave,
  currentSettings,
  description,
  globalNotificationSettings,
  isGlobal,
  ...rest
}) => {
  const styles = useStyles();

  // Individual notification toggles - initialize from current settings
  const [notifications, setNotifications] = useState<Record<string, boolean>>(
    () => universalApiSettingsToUIFormat(currentSettings),
  );

  // Store initial settings to compare against for changes
  const [initialNotifications, setInitialNotifications] = useState<
    Record<string, boolean>
  >(() => universalApiSettingsToUIFormat(currentSettings));

  // Update state when currentSettings changes
  useEffect(() => {
    const newNotifications = universalApiSettingsToUIFormat(currentSettings);
    setNotifications(newNotifications);
    setInitialNotifications(newNotifications);
  }, [currentSettings]);

  // Calculate master toggle state based on current notifications
  const masterToggle = useMemo(() => {
    const checkedCount = Object.values(notifications).filter(Boolean).length;
    if (checkedCount === 0) return false;
    if (checkedCount === NOTIFICATION_TYPES_ARRAY.length) return true;
    return "partial";
  }, [notifications]);

  // Check if there are any changes from initial state
  const hasChanges = useMemo(() => {
    return (
      JSON.stringify(notifications) !== JSON.stringify(initialNotifications)
    );
  }, [notifications, initialNotifications]);

  const handleNotificationChange = (type: string, checked: boolean) => {
    const newNotifications = { ...notifications, [type]: checked };
    setNotifications(newNotifications);
  };

  const handleMasterToggleChange = (checked: boolean) => {
    const newNotifications = NOTIFICATION_TYPES_ARRAY.reduce(
      (acc, type) => {
        acc[type] = checked;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    setNotifications(newNotifications);
  };

  const handleSave = () => {
    onSave?.(notifications);
    onClose();
  };

  const formattedNotifications = useMemo(
    () =>
      NOTIFICATION_TYPES_ARRAY.map((type) => {
        const globalSettings = universalApiSettingsToUIFormat(
          globalNotificationSettings,
        );

        const isDisabled = globalSettings[type] === false && !isGlobal;

        return {
          type,
          isDisabled,
        };
      }),
    [globalNotificationSettings],
  );

  return (
    <Dialog open={open} onClose={onClose} {...rest}>
      <DialogTitle>
        <Typography variant="titleLarge">
          Automated Route Notifications
        </Typography>
        <Typography variant="body1" sx={{ marginTop: "8px" }}>
          {description}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={styles.content}>
          {/* Master Toggle */}
          <FormControlLabel
            control={
              <Checkbox
                checked={masterToggle !== false}
                indeterminate={masterToggle === "partial"}
                onChange={(e) => handleMasterToggleChange(e.target.checked)}
              />
            }
            label={
              <Box sx={styles.masterToggleLabel}>
                <Typography variant="bodyMedium" fontWeight={600}>
                  Enable All Notifications
                </Typography>
              </Box>
            }
            sx={styles.masterToggle}
          />

          {/* Individual Notification Toggles */}
          <Box sx={styles.individualToggles}>
            {formattedNotifications.map(({ type, isDisabled }) => (
              <FormControlLabel
                key={type}
                disabled={isDisabled}
                control={
                  <Checkbox
                    checked={notifications[type]}
                    onChange={(e) =>
                      handleNotificationChange(type, e.target.checked)
                    }
                  />
                }
                label={<Typography variant="bodyMedium">{type}</Typography>}
                sx={styles.individualToggle}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={!hasChanges}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeSxStyles(() => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  masterToggle: {
    marginLeft: 0,
    marginTop: "24px",
    width: "100%",
  },
  masterToggleLabel: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  individualToggles: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    paddingLeft: "24px",
  },
  individualToggle: {
    marginLeft: 0,
    width: "100%",
  },
}));
